// Activity (REQ-243, SCR-592): one timeline of what happened in the org, merged in the browser
// from the org's audit (API-588), the guardrail interventions (usage events with the guardrail
// classes) and, on the proxy, the proxy's own sign-in and key audit. This file is the pure part:
// the row every source produces, the kinds and their chips, the words for each action, the merge
// (newest first, one row per key, a failing source a note) and the filters and CSV over the
// visible rows. No uuid is ever in a row (IA-1): a row names people and targets. The person
// filter is by name, since API-588 returns no actor id. Staff are masked by the API (IA-5); a
// staff actor reads "AllCode support" here.
//
// De-duplication across sources: no field on the wire ties a proxy-local row to the API row of
// the same event (API-588 carries no target id or command id; the proxy's audit no digest), so
// rows are de-duplicated by their own key only — a row two pages both returned shows once.
import { MONTH_SHORT_NAMES } from "../../primitives/months";
import { toCsv } from "../people/source";

/** The Kind chips, in the filter bar's order (REQ-243). */
export const ACTIVITY_KINDS = ["settings", "quotas", "people", "keys", "sign_in", "notifications", "guardrail", "assistant"] as const;
export type ActivityKind = (typeof ACTIVITY_KINDS)[number];

export const KIND_LABELS: Readonly<Record<ActivityKind, string>> = {
  settings: "Settings",
  quotas: "Quotas",
  people: "People",
  keys: "Keys",
  sign_in: "Sign-in",
  notifications: "Notifications",
  guardrail: "Guardrail",
  assistant: "Assistant",
};

/** Where a row came from: each source's rows carry its id, so their keys never collide. */
export type ActivitySourceId = "audit" | "guardrail" | "proxy";

export type ActorKind = "person" | "staff" | "system";

/** What a staff actor reads as on the customer surface (REQ-243, NFR-078). */
export const STAFF_ACTOR = "AllCode support";
/** A service credential, the org's own proxy, or the proxy acting on its own. */
export const SYSTEM_ACTOR = "System";
export const UNKNOWN_PERSON = "Unknown user";
export const NO_TARGET = "—";

export type GuardrailOutcome = "blocked" | "intervened_input" | "intervened_output" | "unavailable";

const OUTCOME_LABEL: Readonly<Record<GuardrailOutcome, string>> = {
  blocked: "Blocked",
  intervened_input: "Intervened on input",
  intervened_output: "Intervened on output",
  unavailable: "Guardrail unavailable",
};

export function outcomeLabel(outcome: GuardrailOutcome): string {
  return OUTCOME_LABEL[outcome];
}

/** A guardrail row's detail, as the Triggered card showed it: categories, never prompt text. */
export interface GuardrailDetail {
  outcome: GuardrailOutcome;
  model: string;
  /** `id@version`; null when the proxy sent no record (an unavailable guardrail may not). */
  guardrail: string | null;
  policies: readonly string[];
  /** The guardrail acted but the proxy reported no categories: the installation's trace is off. */
  untraced: boolean;
  /** The event's stored prompt in the shell's Prompt logs; null unless the shell offers one. */
  promptHref: string | null;
}

/** One row of the timeline, whatever its source. */
export interface ActivityRow {
  /** Stable across loads: the source and the source's own id. */
  key: string;
  source: ActivitySourceId;
  /** RFC 3339, UTC. */
  at: string;
  kind: ActivityKind;
  actorKind: ActorKind;
  /** Who, in words: a name or email, "AllCode support", "System" — never an id. */
  who: string;
  /** Under the name when it adds something (the email). */
  whoDetail: string | null;
  /** What happened, in words. */
  what: string;
  /** The target, by name. */
  target: string;
  /** The row detail's extra facts (label → words), content-free. */
  facts: readonly { label: string; value: string }[];
  /** Set on a guardrail row. */
  guardrail: GuardrailDetail | null;
}

// ------------------------------------------------------------------------------------- kinds

/**
 * API-588's kinds are the same words but for `guardrail`, which only usage events have; an
 * unknown kind (a newer API) is Settings, as the API's own fallback is.
 */
export function kindOfAudit(kind: string): ActivityKind {
  return (ACTIVITY_KINDS as readonly string[]).includes(kind) && kind !== "guardrail" ? (kind as ActivityKind) : "settings";
}

/** The proxy-local audit's actions (ENT-486) by family. */
export function kindOfProxyAction(action: string, keyId: string | undefined): ActivityKind {
  const family = action.slice(0, action.indexOf(".") + 1);
  switch (family) {
    case "key.":
      return "keys";
    case "user.":
    case "invite.":
      return "people";
    case "settings.":
      return "settings";
    case "command.":
      return keyId ? "keys" : "people";
    default:
      return "sign_in";
  }
}

// ------------------------------------------------------------------------------------- words

/** API-588 actions in words; a family's words stand in for an action this table does not know. */
const AUDIT_ACTIONS: Readonly<Record<string, string>> = {
  "settings.default.update": "Changed the organisation's settings",
  "settings.org.update": "Changed the organisation's settings",
  "settings.override.update": "Changed an installation's settings",
  "settings.override.delete": "Reset an installation to the organisation's settings",
  "org.display_name": "Renamed the organisation",
  "quota.create": "Created a quota",
  "quota.update": "Changed a quota",
  "quota.delete": "Deleted a quota",
  "quota.disable": "Turned a quota off",
  "quota.adopt": "Adopted a quota",
  "quota.preview": "Previewed a quota",
  "quota.grant": "Granted extra quota",
  "quota.grant.revoke": "Revoked a quota grant",
  "webhook.create": "Added a webhook",
  "webhook.update": "Changed a webhook",
  "webhook.delete": "Deleted a webhook",
  "webhook.test": "Tested a webhook",
  "webhook.rotate_secret": "Rotated a webhook's secret",
  "sign_in.flip": "Changed who serves sign-in",
  "oidc.provider.create": "Added an identity provider",
  "credential.issue_via_sso": "Issued a key through single sign-on",
  "credential.setup_redeemed": "Redeemed a setup code",
  "identity.revoke": "Revoked a person's access",
  "identity.default_team": "Changed the default team",
  "identity.user_adopt_id": "Linked a person's sign-in id",
  "identity.team_merge_phantom": "Merged a placeholder team",
  "agent.session.create": "Started an Assistant conversation",
  "agent.session.end": "Ended an Assistant conversation",
  "agent.session.message": "Sent the Assistant a message",
  "support.command_issued": "Issued a support command",
  "support.command_applied": "The proxy applied a support command",
};

const AUDIT_FAMILIES: readonly (readonly [string, string])[] = [
  ["settings.", "Changed settings"],
  ["quota.", "Changed quotas"],
  ["webhook.", "Changed notifications"],
  ["sign_in.", "Changed sign-in"],
  ["oidc.", "Changed an identity provider"],
  ["credential.", "Changed a key"],
  ["identity.", "Changed people"],
  ["agent.", "Used the Assistant"],
  ["support.", "AllCode support acted"],
];

/** A support command's kind in words, as the staff twin names them. */
const COMMAND_KINDS: Readonly<Record<string, string>> = {
  revoke_key: "revoke a key",
  disable_user: "disable a person",
  end_sessions: "end a person's sessions",
  invite_user: "invite a person",
};

/**
 * What an API-588 row did, in words. Like the staff audit's `actionSummary`, a sentence when the
 * action reads better as one, the action code itself when nothing here knows it. A support
 * command names its kind ("Issued a support command: revoke a key").
 */
export function auditActionText(action: string, detail: Readonly<Record<string, unknown>> | null): string {
  const known = AUDIT_ACTIONS[action] ?? AUDIT_FAMILIES.find(([prefix]) => action.startsWith(prefix))?.[1] ?? action;
  const command = typeof detail?.["kind"] === "string" ? COMMAND_KINDS[detail["kind"]] : undefined;
  return action === "support.command_issued" && command ? `${known}: ${command}` : known;
}

const PROXY_ACTIONS: Readonly<Record<string, string>> = {
  "key.minted": "Issued a key",
  "key.replaced": "Replaced a key",
  "key.revoked": "Revoked a key",
  "key.relabelled": "Renamed a key",
  "user.disabled": "Disabled a person",
  "user.enabled": "Enabled a person",
  "user.approved": "Approved a person",
  "user.rejected": "Rejected a person",
  "user.erased": "Erased a person",
  "user.team_set": "Changed a person's team",
  "user.admin_set": "Changed a person's admin access",
  "user.prompt_reader_set": "Changed who may read prompts",
  "user.external_id_set": "Changed a person's sign-in id",
  "sign_in.ok": "Signed in",
  "sign_in.refused": "Was refused sign-in",
  "code.redeemed": "Redeemed a sign-in code",
  "sessions.ended": "Ended a person's sessions",
  "command.applied": "Applied a support command",
  "secret.written": "Stored an identity provider's secret",
  "settings.applied": "Applied new settings",
  "signin.flip_confirmed": "Confirmed a test sign-in",
  "invite.created": "Invited a person",
  "invite.sent": "Sent an invite",
  "invite.send_failed": "Could not send an invite",
  "invite.revoked": "Revoked an invite",
  "invite.extended": "Extended an invite",
  "invite.redeemed": "Accepted an invite",
};

/** What a proxy-local row did, in words; the action code for one this table does not know. */
export function proxyActionText(action: string): string {
  return PROXY_ACTIONS[action] ?? action;
}

/** The detail's settings paths ("models.allowed_models, models.fallbacks"), or null without any. */
export function changedPaths(detail: Readonly<Record<string, unknown>> | null): string | null {
  const changed = detail?.["changed"];
  if (!Array.isArray(changed)) return null;
  const paths = changed.filter((path): path is string => typeof path === "string");
  return paths.length > 0 ? paths.join(", ") : null;
}

/** Who acted, in words, for an actor of any source. */
export function actorText(kind: ActorKind, name: string | null | undefined, email: string | null | undefined): { who: string; whoDetail: string | null } {
  if (kind === "staff") return { who: STAFF_ACTOR, whoDetail: null };
  if (kind === "system") return { who: SYSTEM_ACTOR, whoDetail: null };
  const who = name?.trim() || email?.trim() || UNKNOWN_PERSON;
  const mail = email?.trim() || null;
  return { who, whoDetail: mail && mail !== who ? mail : null };
}

// ------------------------------------------------------------------------------------- merge

/** One source's answer for the merge: its rows, or the failure the screen notes by its label. */
export type SourceResult = { label: string; ok: true; rows: readonly ActivityRow[] } | { label: string; ok: false };

export interface Merged {
  rows: ActivityRow[];
  /** One sentence per failed source; the other sources' rows still show. */
  notes: string[];
}

/** Newest first; a tie is ordered by key, so the order never depends on which source answered first. */
export function compareRows(a: ActivityRow, b: ActivityRow): number {
  const at = Date.parse(b.at) - Date.parse(a.at);
  return at !== 0 ? at : a.key.localeCompare(b.key);
}

/** The rows of every source, newest first, one per key; a failing source is a note naming it. */
export function mergeActivity(results: readonly SourceResult[]): Merged {
  const byKey = new Map<string, ActivityRow>();
  const notes: string[] = [];
  for (const result of results) {
    if (!result.ok) {
      notes.push(failureNote(result.label));
      continue;
    }
    for (const row of result.rows) if (!byKey.has(row.key)) byKey.set(row.key, row);
  }
  return { rows: [...byKey.values()].sort(compareRows), notes };
}

export function failureNote(label: string): string {
  return `${label.charAt(0).toUpperCase()}${label.slice(1)} could not be loaded; the rest of the activity is shown.`;
}

// ----------------------------------------------------------------------------------- filters

/** The filter bar: kinds (none is every kind), one person by name, and the days from and to (inclusive). */
export interface ActivityFilters {
  kinds: readonly ActivityKind[];
  person: string | null;
  /** YYYY-MM-DD, UTC, inclusive. */
  from: string;
  /** YYYY-MM-DD, UTC, inclusive. */
  to: string;
}

/** The API window of the filters: from the first day to the day after the last (exclusive). */
export interface ActivityWindow {
  from: string;
  to: string;
}

export const DEFAULT_WINDOW_DAYS = 30;
/** API-588's longest window. */
export const MAX_WINDOW_DAYS = 92;
const DAY_MS = 86_400_000;

function isoDay(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** The last 30 days (UTC) up to today, every kind, everyone. */
export function defaultFilters(now: Date): ActivityFilters {
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return { kinds: [], person: null, from: isoDay(today - (DEFAULT_WINDOW_DAYS - 1) * DAY_MS), to: isoDay(today) };
}

/** The window the sources read: `to` is exclusive on the wire. */
export function windowOf(filters: Pick<ActivityFilters, "from" | "to">): ActivityWindow {
  return { from: filters.from, to: isoDay(Date.parse(filters.to) + DAY_MS) };
}

/** Why the from/to pair cannot be read, or null when it can. */
export function windowError(filters: Pick<ActivityFilters, "from" | "to">): string | null {
  const from = Date.parse(filters.from);
  const to = Date.parse(filters.to);
  if (Number.isNaN(from) || Number.isNaN(to)) return "Choose a from and a to day.";
  if (to < from) return "The to day is before the from day.";
  if ((to - from) / DAY_MS + 1 > MAX_WINDOW_DAYS) return `Choose at most ${MAX_WINDOW_DAYS} days.`;
  return null;
}

/** Turns one kind chip on or off. */
export function toggleKind(kinds: readonly ActivityKind[], kind: ActivityKind): ActivityKind[] {
  return kinds.includes(kind) ? kinds.filter((k) => k !== kind) : ACTIVITY_KINDS.filter((k) => k === kind || kinds.includes(k));
}

/** Whether a source's rows can match the kinds asked for (none asked is every kind). */
export function wantsKind(filters: Pick<ActivityFilters, "kinds">, kind: ActivityKind): boolean {
  return filters.kinds.length === 0 || filters.kinds.includes(kind);
}

/** The rows the filters leave, in their order. A source may filter on the wire too; this is the one rule. */
export function visibleRows(rows: readonly ActivityRow[], filters: Pick<ActivityFilters, "kinds" | "person">): ActivityRow[] {
  return rows.filter((row) => wantsKind(filters, row.kind) && (filters.person === null || (row.actorKind === "person" && row.who === filters.person)));
}

/** The person filter's options: each person who acted in the rows, by name, in name order. */
export function personOptions(rows: readonly ActivityRow[]): string[] {
  return [...new Set(rows.filter((row) => row.actorKind === "person").map((row) => row.who))].sort((a, b) => a.localeCompare(b));
}

/** "3 Sep 2026, 14:05 UTC". */
export function eventTime(iso: string): string {
  const [year, month, day] = iso.slice(0, 10).split("-");
  return `${Number(day)} ${MONTH_SHORT_NAMES[Number(month) - 1] ?? month} ${year}, ${iso.slice(11, 16)} UTC`;
}

/** The visible rows as CSV: what the table shows, never an id. */
export function activityCsv(rows: readonly ActivityRow[]): string {
  return toCsv(
    ["when", "who", "email", "what", "target", "kind"],
    rows.map((row) => [row.at, row.who, row.whoDetail ?? "", row.what, row.target, KIND_LABELS[row.kind]]),
  );
}
